import { z } from 'zod';

/**
 * Atheon AI contract (CLAUDE.md §13 — "Analytics — Atheon AI", M11).
 *
 * Single source of truth for the analytics layer, shared by:
 *   - the edge agent's Node model-runner (apps/edge-agent/src/model-runner.ts), which shells out
 *     to the on-prem Python sidecar (apps/edge-agent/py/sidecar.py) and validates its JSON I/O;
 *   - the cloud light-scoring module (apps/api/src/lib/scoring.ts), which scores incoming
 *     aggregates in the ingest consumer.
 *
 * Hard rules carried by this contract (§13):
 *   - Model outputs are clearly-typed DERIVED series, NEVER metered readings. Every derived row
 *     carries `derived: true` and the producing `model_id` / `model_version`.
 *   - Every output and event records its `model_id` + `model_version` so provenance is logged with
 *     each output (M&V defensibility, §8.4 / §14).
 *   - On-prem models are authoritative against full-resolution data; the cloud only runs light,
 *     explainable scoring (threshold / seasonal) and stores the model id + inputs with each output.
 *
 * The Node contract MUST stay in sync with apps/edge-agent/py/contract.py (the Python dataclasses).
 */

// --- On-prem sidecar models -----------------------------------------------------------------

/** The three on-prem model families (CLAUDE.md §8.4). Must mirror VALID_MODELS in contract.py. */
export const ATHEON_MODELS = ['baseline', 'anomaly', 'forecast'] as const;
export const AtheonModelSchema = z.enum(ATHEON_MODELS);
export type AtheonModel = z.infer<typeof AtheonModelSchema>;

/** Stable model identities emitted in `model_id` (mirror contract.py response model_id values). */
export const ATHEON_MODEL_IDS = {
  baseline: 'atheon.baseline',
  anomaly: 'atheon.anomaly',
  forecast: 'atheon.forecast',
} as const satisfies Record<AtheonModel, string>;

/**
 * A request written to the sidecar's stdin. `series` / `params` are model-specific bags validated
 * by the Python model module itself (kept loose here so the contract doesn't fork per model — see
 * contract.py docstring for the per-model `series` shape).
 */
export const ModelRequestSchema = z.object({
  model: AtheonModelSchema,
  /** Optional; the sidecar pins a default version per model when omitted. */
  model_version: z.string().min(1).optional(),
  series: z.record(z.unknown()).default({}),
  params: z.record(z.unknown()).default({}),
});
export type ModelRequest = z.infer<typeof ModelRequestSchema>;

/**
 * One row of a DERIVED time-series (a baseline prediction, a forecast point). `derived` is always
 * true — these are model outputs, never metered values. Quality is propagated (§8.3); derived rows
 * default to `good`.
 */
export const DerivedPointSchema = z.object({
  tag: z.string().min(1),
  ts: z.string().nullable(),
  value: z.number(),
  derived: z.literal(true).default(true),
  quality: z.enum(['good', 'uncertain', 'bad', 'substituted']).default('good'),
});
export type DerivedPoint = z.infer<typeof DerivedPointSchema>;

/** A derived scalar output (a regression coefficient, R², CV-RMSE). */
export const DerivedScalarSchema = z.object({
  name: z.string().min(1),
  value: z.number(),
  derived: z.literal(true).default(true),
});
export type DerivedScalar = z.infer<typeof DerivedScalarSchema>;

/** A model output is either a derived series point or a derived scalar. */
export const ModelOutputSchema = z.union([DerivedPointSchema, DerivedScalarSchema]);
export type ModelOutput = z.infer<typeof ModelOutputSchema>;

/** True when a model output is a {@link DerivedPoint} (has a `tag`) vs a {@link DerivedScalar}. */
export function isDerivedPoint(o: ModelOutput): o is DerivedPoint {
  return 'tag' in o;
}

/**
 * An anomaly / alert record. Field shape mirrors the Node IngestRow event payload
 * (severity, kind, message) plus an optional ts; the model-runner maps this onto an IngestRow with
 * kind="event". Severity mirrors EventSeveritySchema in ingest.ts.
 */
export const ModelEventSchema = z.object({
  severity: z.enum(['info', 'warn', 'critical']),
  kind: z.string().min(1),
  message: z.string().min(1),
  ts: z.string().nullable().optional(),
});
export type ModelEvent = z.infer<typeof ModelEventSchema>;

/** The response read from the sidecar's stdout. Mirrors ModelResponse in contract.py. */
export const ModelResponseSchema = z.object({
  model_id: z.string().min(1),
  model_version: z.string().min(1),
  ok: z.boolean().default(true),
  outputs: z.array(ModelOutputSchema).default([]),
  events: z.array(ModelEventSchema).default([]),
  stats: z.record(z.unknown()).default({}),
  error: z.string().nullable().default(null),
});
export type ModelResponse = z.infer<typeof ModelResponseSchema>;

// --- Cloud light scoring --------------------------------------------------------------------

/** Cloud scoring model identity (§13 — explainable, stores model id + inputs with each output). */
export const ATHEON_CLOUD_SCORING_ID = 'atheon.cloud.scoring';
export const ATHEON_CLOUD_SCORING_VERSION = '1';

/** A scoring detector kind, surfaced in the emitted alert `kind` for explainability. */
export const ScoreDetectorSchema = z.enum(['threshold', 'seasonal']);
export type ScoreDetector = z.infer<typeof ScoreDetectorSchema>;

/**
 * One scored finding produced by the cloud scoring pass over incoming aggregates. Carries the
 * model provenance (`model_id` / `model_version`) and the explainable `inputs` that justified it,
 * which the consumer persists alongside the alert (R2 event archive) for M&V defensibility.
 */
export const ScoreFindingSchema = z.object({
  tag: z.string().min(1),
  ts: z.string().datetime(),
  detector: ScoreDetectorSchema,
  severity: z.enum(['info', 'warn', 'critical']),
  kind: z.string().min(1),
  message: z.string().min(1),
  model_id: z.string().min(1),
  model_version: z.string().min(1),
  /** Explainable scoring inputs (observed value, limit/baseline, residual, z-score, etc.). */
  inputs: z.record(z.number().nullable()),
});
export type ScoreFinding = z.infer<typeof ScoreFindingSchema>;

// --- Vectorize load-pattern matching (Phase 2, §13 optional) --------------------------------

/** Embedding dimension for a daily load profile vector (24 hourly normalised demand points). */
export const LOAD_PATTERN_DIM = 24;

/** A load-pattern vector + metadata upserted to / queried from the Vectorize `PATTERNS` index. */
export const LoadPatternVectorSchema = z.object({
  id: z.string().min(1),
  site: z.string().min(1),
  /** Day this profile summarises (YYYY-MM-DD). */
  day: z.string().min(1),
  values: z.array(z.number()).length(LOAD_PATTERN_DIM),
});
export type LoadPatternVector = z.infer<typeof LoadPatternVectorSchema>;
